import React, { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { IconButton, Button } from "@mui/material";
import { ReactComponent as EmptyCartSVG } from "../assets/img/Emptycart.svg";
import {
  Add,
  Remove,
  Close,
  StorefrontOutlined,
  LocationOnOutlined,
  CheckCircleOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
// The restaurant placeholder Izi picked: fork, plate and knife on a plain
// near-white field. Used wherever a dish has no photograph yet.
import menuPlaceholder from "../assets/img/restaurant-dish-placeholder.png";
import {
  addToCart,
  removeFromCart,
  switchRestaurant,
  clearCart,
} from "../redux/restaurantCartSlice";
import { formatCurrency } from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { StruckPrice, VendorOfferLine } from "./PriceCut";
import {
  lineHasDeal,
  cartOfferSavings,
  hasSalePrice,
  unitPriceOf,
} from "../utils/vendorDiscounts";
import {
  linesSubtotal,
  cartMinimumVerdict,
  minimumOrderMessage,
} from "../utils/minimumOrder";
import MinimumOrderNotice from "./MinimumOrderNotice";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import useCoverage from "../redux/useCoverage";
import CoverageBanner from "./CoverageBanner";
import LocationPrompt from "./LocationPrompt";
import useRestaurantAvailability from "../hooks/useRestaurantAvailability";
import { availabilityAllowsOrdering } from "../utils/restaurantAvailability";
import RestaurantAvailabilityBanner from "./RestaurantAvailabilityBanner";
import BranchCartCheck from "./branch/BranchCartCheck";
import { blockedHere } from "../utils/branchAvailability";
import "./RestaurantCart.css";
import CartSummary from "./CartSummary";
import useDeliveryEstimate from "../hooks/useDeliveryEstimate";
import { CartPromiseNote } from "./LusukuPromise";

/**
 * RestaurantCart — the order modal.
 *
 * Redesigned per the Braun / Dieter Rams principles: a calm white header instead
 * of the heavy gradient, flat item rows, tabular numerals, and the Total as the
 * single loudest element (as little design as possible; clear hierarchy).
 *
 * It also ENFORCES the delivery address here, upstream of checkout: the customer
 * sets their location in the modal, and "Proceed" is blocked until the vendor
 * has a location that it delivers to — so nobody reaches Confirm only to learn
 * the vendor can't deliver (backend /api/coverage is the single source of truth).
 */
const RestaurantCart = ({ onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentRestaurant = useSelector(
    (state) => state.restaurantCart.currentRestaurant,
  );
  const cart = useSelector(
    (state) => state.restaurantCart.carts[currentRestaurant] || {},
  );
  const restaurant = useSelector((state) => state.restaurantCart.restaurant);
  const userLocation = useSelector((state) => state.userLocation);
  const cartItems = Object.values(cart);
  const outletId = cartItems.find((item) => item?.outletId)?.outletId || null;
  // What the vendor's live offers took off this cart; the total is net of it.
  const offerSavings = cartOfferSavings(cartItems);

  // Authoritative coverage for THIS vendor vs the customer's set location — the
  // same road-distance + radius decision the order-create gate makes.
  const coverage = useCoverage({
    vendorId: currentRestaurant,
    outletId,
    vendorType: "restaurant",
  });
  const availability = useRestaurantAvailability({
    vendorId: currentRestaurant,
    outletId,
  });
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  /* Clearing a cart cannot be undone, so it asks first. The confirmation is an
     inline strip at the top of the list rather than a dialog: a second modal
     stacked on this one is awkward on a phone, and putting the question where
     the items are keeps what is about to be deleted in view while it is
     asked. */
  const [confirmClear, setConfirmClear] = useState(false);

  const handleClearCart = () => {
    dispatch(clearCart(currentRestaurant));
    setConfirmClear(false);
  };

  /* "Add more from <restaurant>" — the dishes this kitchen already has, minus
     what is in the cart. A cart holding one item left most of a phone screen
     empty, and an almost-empty screen is a bad place to be standing at the
     moment somebody decides whether to spend. This is the same menu the shop
     page behind this modal has already fetched, so it costs no request.

     The MENU is `state.restaurants.details`, NOT `selectedRestaurant`. The
     latter is the restaurant record (name, address, images) and carries no
     sections at all, so reading it here silently produced zero suggestions
     and the whole block never rendered.

     Two filters, both load-bearing. Only dishes WITH a photo, the same rule
     the listing grid uses, because a cross-sell tile with no picture sells
     nothing. And only dishes with NO REQUIRED option group: this rail adds
     straight to the cart in one tap, which would otherwise walk straight past
     the `minOptions` check the dish dialog enforces and put an unconfigured
     item in somebody's order. A dish that needs choices has to be opened. */
  const menuDetails = useSelector((state) => state.restaurants.details);
  const suggestions = useMemo(() => {
    const inCart = new Set(cartItems.map((item) => String(item._id)));
    return (menuDetails?.sections || [])
      .flatMap((section) => section.products || [])
      .filter(
        (dish) =>
          dish &&
          dish.enabled !== false &&
          // Nothing the branch cannot sell right now (4 Oct 2026).
          !blockedHere(dish) &&
          dish.soldOutToday !== true &&
          dish.image &&
          !inCart.has(String(dish._id)) &&
          !(dish.optionGroups || []).some((group) => group?.minOptions),
      )
      .slice(0, 8);
  }, [menuDetails, cartItems]);

  /* "Add more from Cakely By Asha Batenga" wraps and swallows the row. The
     leading words carry the recognition, so the heading takes those and the
     full name stays in the header two lines above it. */
  const shortVendorName = useMemo(() => {
    const full = String(restaurant?.name || "").trim();
    if (!full) return "this kitchen";
    /* Cut at the connector first, THEN cap the length. Taking a blunt first
       two words turns "Cakely By Asha Batenga" into "Cakely By", which reads
       like the sentence was truncated mid-thought. Everything after a "by" or
       a dash is attribution, and the words before it are the ones people use
       for the place. */
    const CONNECTOR = /\s+(?:by|from|at|ltd\.?|limited)\s+|\s*[-–—,|]\s*/i;
    const head = full.split(CONNECTOR)[0].trim() || full;
    const words = head.split(/\s+/);
    return words.length > 2 ? words.slice(0, 2).join(" ") : head;
  }, [restaurant]);

  const menuDishCount = useMemo(
    () =>
      (menuDetails?.sections || [])
        .flatMap((section) => section.products || [])
        .filter((dish) => dish && dish.enabled !== false).length,
    [menuDetails],
  );

  const hasLocation = coverage.hasLocation;
  const outOfRange = coverage.isOutOfRange;

  /* Minimum order value. Summed from the lines rather than read off the slice
     total, so this cart and the hub can never disagree about the same basket.
     The server refuses a short basket at order-create; catching it here means
     the customer adds a dish instead of meeting a 400 after choosing how to
     pay. */
  const goodsSubtotal = linesSubtotal(cartItems);
  const minimumOrderValue = useMinimumOrderValue();
  /* The cart as the server prices it (POST /api/quote): the delivery fee, the
     total and the minimum-order verdict all come from this one answer, and the
     cart's own sum stands in only until it arrives. */
  const estimate = useDeliveryEstimate({
    vendorId: currentRestaurant,
    outletId,
    items: cartItems,
    enabled: cartItems.length > 0,
  });
  const minimum = cartMinimumVerdict(estimate, { subtotal: goodsSubtotal, minimum: minimumOrderValue });
  const minimumShortfall = minimum.shortfall;

  // Fail closed: a readable location is not enough; the backend must return a
  // positive coverage verdict before checkout can begin.
  const canProceed =
    cartItems.length > 0 &&
    minimumShortfall === 0 &&
    hasLocation &&
    coverage.isConfirmed &&
    availability.canOrder;

  const handleIncrease = (product) => {
    if (!availability.canOrder) return;
    dispatch(
      addToCart({
        restaurantId: currentRestaurant,
        product,
        quantity: 1,
        options: product.options,
      }),
    );
  };

  const handleDecrease = (product) => {
    dispatch(
      addToCart({
        restaurantId: currentRestaurant,
        product,
        quantity: -1,
        options: product.options,
      }),
    );
    if (product.quantity === 1) {
      dispatch(
        removeFromCart({
          restaurantId: currentRestaurant,
          productId: product._id,
        }),
      );
    }
  };

  /* A dish tapped in the "Add more from …" rail comes straight off the menu
     document, and menu documents carry no outlet identity — the storefront
     stamps outletId on from the ?outletId query only when a dish is opened
     from the grid. So a rail add on an outlet page produced a cart line with
     no outlet while every line beside it had one.

     That is not cosmetic. The server announces a new order to the kitchen
     PER ITEM — it emits "newOrder" to the room outlet:<item.outletId> — so the
     rail-added dish was never announced to the outlet that has to cook it —
     it simply did not appear on that kitchen's screen. Carry the cart's outlet
     onto the dish and the whole order lands in one place. */
  const handleAddSuggestion = (dish) =>
    handleIncrease(outletId ? { ...dish, outletId } : dish);

  const checkoutHandler = async () => {
    if (!cartItems.length || !hasLocation || !coverage.isConfirmed) return;
    const latest = await availability.refresh();
    if (!availabilityAllowsOrdering(latest)) return;
    dispatch(switchRestaurant(currentRestaurant));
    history.push("/restaurant-shipping");
  };

  return (
    <>
      <div className="restaurant-cart-container">
        {/* Header — quiet, honest, dark text on white */}
        <div className="restaurant-cart-header">
          <div className="restaurant-cart-header-text">
            <h2 className="restaurant-cart-title">Your order</h2>
            {restaurant && (
              <p className="restaurant-cart-subtitle">
                <StorefrontOutlined className="restaurant-cart-subtitle-icon" />
                {restaurant.name}
              </p>
            )}
          </div>
          {cartItems.length > 0 && (
            <IconButton
              className="restaurant-cart-clear-btn"
              onClick={() => setConfirmClear((open) => !open)}
              size="small"
              aria-label="Clear cart"
              title="Clear cart"
            >
              <DeleteOutlineOutlined fontSize="small" color="error" />
            </IconButton>
          )}
          <IconButton
            className="restaurant-cart-close-btn"
            onClick={onClose}
            aria-label="close cart"
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="restaurant-cart-content">
          {confirmClear && cartItems.length > 0 && (
            <div className="restaurant-cart-clear-confirm" role="alertdialog">
              <p>
                Remove all {cartItems.length}{" "}
                {cartItems.length === 1 ? "item" : "items"} from this order?
              </p>
              <div className="restaurant-cart-clear-actions">
                <button
                  type="button"
                  className="restaurant-cart-clear-keep"
                  onClick={() => setConfirmClear(false)}
                >
                  Keep them
                </button>
                <button
                  type="button"
                  className="restaurant-cart-clear-go"
                  onClick={handleClearCart}
                >
                  Clear cart
                </button>
              </div>
            </div>
          )}

          {cartItems.length === 0 ? (
            <div className="restaurant-cart-empty">
              <div className="restaurant-cart-empty-icon-wrapper">
                <EmptyCartSVG className="restaurant-cart-empty-icon" />
              </div>
              <h3 className="restaurant-cart-empty-title">
                Your cart is empty
              </h3>
              <p className="restaurant-cart-empty-text">
                Add a few items and they’ll show up here.
              </p>
            </div>
          ) : (
            <ul className="restaurant-cart-items-list">
              {cartItems.map((item) => {
                const optionsTotal =
                  item.options?.reduce(
                    (sum, option) => sum + option.price,
                    0,
                  ) || 0;
                const itemTotal = (item.price + optionsTotal) * item.quantity;
                return (
                  <li key={item._id} className="restaurant-cart-item">
                    {/* A dish can be ordered before it has been photographed,
                        so a missing image is normal here, not a failure. A bare
                        img would draw the browser's broken-image glyph. */}
                    <img
                      src={item.image || menuPlaceholder}
                      alt={item.name}
                      className="restaurant-cart-item-image"
                      onError={(e) => (e.target.src = menuPlaceholder)}
                    />
                    <div className="restaurant-cart-item-details">
                      <h4 className="restaurant-cart-item-name">{item.name}</h4>
                      <p className="restaurant-cart-item-each">
                        {lineHasDeal(item) && (
                          <StruckPrice>
                            {formatCurrency(item.listPrice + optionsTotal)}
                          </StruckPrice>
                        )}
                        {formatCurrency(item.price + optionsTotal)} each
                      </p>
                      <div className="restaurant-cart-item-bottom">
                        <div className="restaurant-cart-quantity-controls">
                          <IconButton
                            className="restaurant-cart-quantity-btn"
                            onClick={() => handleDecrease(item)}
                            size="small"
                            aria-label="decrease quantity"
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <span className="restaurant-cart-quantity-display">
                            {item.quantity}
                          </span>
                          <IconButton
                            className="restaurant-cart-quantity-btn"
                            onClick={() => handleIncrease(item)}
                            size="small"
                            aria-label="increase quantity"
                            disabled={!availability.canOrder}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </div>
                        <span className="restaurant-cart-item-sum">
                          {formatCurrency(itemTotal)}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {cartItems.length > 0 && (
            <div className="restaurant-cart-summary">
              {/* Deliver-to and coverage: the address is enforced here,
                  before checkout. With no area set, CoverageBanner asks for
                  it and the checkout button says so. */}
              <div className="restaurant-cart-deliverto">
                <RestaurantAvailabilityBanner
                  availability={availability}
                  compact
                />
                {hasLocation && (
                  <div className="restaurant-cart-deliverto-row">
                    <span
                      className={`restaurant-cart-deliverto-icon ${outOfRange ? "warn" : "ok"}`}
                    >
                      {outOfRange ? (
                        <LocationOnOutlined fontSize="small" />
                      ) : (
                        <CheckCircleOutlined fontSize="small" />
                      )}
                    </span>
                    <span className="restaurant-cart-deliverto-text">
                      <span className="restaurant-cart-deliverto-label">
                        Deliver to
                      </span>
                      <span className="restaurant-cart-deliverto-addr">
                        {userLocation.address || "Your set location"}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="restaurant-cart-deliverto-change"
                      onClick={() => setShowLocationPrompt(true)}
                    >
                      Change
                    </button>
                  </div>
                )}

                <CoverageBanner
                  variant="inline"
                  deliverable={coverage.deliverable}
                  reason={coverage.reason}
                  distanceKm={coverage.distanceKm}
                  radius={coverage.radius}
                  hasLocation={hasLocation}
                  status={coverage.status}
                  error={coverage.error}
                  onRetry={coverage.refresh}
                  onChangeLocation={() => setShowLocationPrompt(true)}
                />
              </div>

              <MinimumOrderNotice
                shortfall={minimum.shortfall}
                minimum={minimum.minimum}
                subtotal={minimum.subtotal}
                vendorType="restaurant"
              />
              <VendorOfferLine amount={offerSavings} format={formatCurrency} />
              <CartSummary
                estimate={estimate}
                subtotal={goodsSubtotal}
                vendorId={currentRestaurant}
                vendorType="restaurant"
              />
              <CartPromiseNote />
            </div>
          )}

          {cartItems.length > 0 && suggestions.length > 0 && (
            <section className="restaurant-cart-more">
              <div className="restaurant-cart-more-head">
                <h3>Add more from {shortVendorName}</h3>
                {menuDishCount > 0 && <span>{menuDishCount} dishes</span>}
              </div>
              <ul className="restaurant-cart-more-rail">
                {suggestions.map((dish) => (
                  <li key={dish._id} className="restaurant-cart-more-card">
                    <img
                      src={getOptimizedUrl(dish.image, "cardHero")}
                      alt={dish.name}
                      loading="lazy"
                    />
                    <div className="restaurant-cart-more-body">
                      <p className="restaurant-cart-more-name">{dish.name}</p>
                      <div className="restaurant-cart-more-row">
                        <span className="restaurant-cart-more-price">
                          {hasSalePrice(dish) && (
                            <StruckPrice>{formatCurrency(dish.price)}</StruckPrice>
                          )}
                          {formatCurrency(unitPriceOf(dish))}
                        </span>
                        <IconButton
                          className="restaurant-cart-more-add"
                          onClick={() => handleAddSuggestion(dish)}
                          size="small"
                          disabled={!availability.canOrder}
                          aria-label={`Add ${dish.name}`}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Only the checkout button is pinned; everything above it scrolls
            with the items, so a short screen never hides the money. */}
        <div className="restaurant-cart-footer">
          <Button
            className="restaurant-cart-checkout-btn"
            onClick={checkoutHandler}
            disabled={!canProceed}
            variant="contained"
            fullWidth
          >
            {cartItems.length === 0
              ? "Your cart is empty"
              : minimumShortfall > 0
                ? minimumOrderMessage(minimumShortfall, formatCurrency)
                : !availability.canOrder
                  ? availability.isClosed
                    ? "Restaurant is closed"
                    : availability.isUnknown
                      ? "Hours unavailable — retry above"
                      : "Confirming restaurant hours…"
                  : !hasLocation
                    ? "Set your delivery location"
                    : outOfRange
                      ? "Vendor doesn’t deliver here"
                      : coverage.isFailed
                        ? "Delivery check failed — retry above"
                        : !coverage.isConfirmed
                          ? "Checking delivery coverage…"
                          : "Proceed to checkout"}
          </Button>
        </div>
      </div>

      {showLocationPrompt && (
        <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
      )}
      {/* Opening the cart checks it at its branch (4 Oct 2026): a cart read
          back from localStorage, moved, or touched by a live branch change
          shows "Changes at <branch>" before checkout, not after. */}
      <BranchCartCheck vertical="restaurant" cartId={currentRestaurant} />
    </>
  );
};

export default RestaurantCart;
